import { useDarkMode } from "@/hooks/useDarkMode";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  mapCenterAtom,
  mobileListViewAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  savedPlaceSortByToViewAtom,
  hoveredVenueIdAtom,
  viewModeAtom,
} from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import {
  defaultRegion,
  darkModeStyles,
  lightModeStyles,
  enableAutoZoom,
} from "common/config";
import {
  useSearchPageParams,
  useHomeVenues,
  useI18N,
  useMapSettings,
  useSavedPlaces,
  useAuth,
} from "common/hooks";
import { Venue, VenuePinType } from "common/types";
import { calculateZoomFromDistance } from "common/apis/search";
import { useAtom } from "jotai";
import React, { useEffect, useRef, useState } from "react";
import { useMatchRoute } from "@tanstack/react-router";

import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";

import YouAre from "@/assets/pins/youAre.svg";
import AnalyticsService from "@/services/analytics";
import VenueCard from "@/components/VenueCard/VenueCard";
import { FiMapPin } from "react-icons/fi";
import { cn } from "@/lib/utils";
import { TbCurrentLocation } from "react-icons/tb";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { X } from "phosphor-react";
import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";
import { StandardButton } from "@/components/common/StandardButton";
import Modal from "react-responsive-modal";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";

function BasicMapView(props: {
  query: string;
  hidden?: boolean;
  currentVenueTypePage?: number;
  mapSource?: string;
  locations?: Venue[];
}) {
  const { currentVenueTypePage = 0, locations: locationsOverride } = props;
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [savedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [savedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);

  const i18n = useI18N();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const { mapSettings } = useMapSettings();

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [tooltipVenue, setTooltipVenue] = useState<Venue | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [snippetPosition, setSnippetPosition] = useState<{
    offset: { x: number; y: number };
    className: string;
  } | null>(null);
  const [snippetOpen, setSnippetOpen] = useState<boolean>(false); // Track if snippet is actively displayed
  // TODO: Panning state commented out since we're not using map panning
  // const [isPanning, setIsPanning] = useState(false);
  // Cooldown to suppress auto-zoom right after a user-initiated map move
  const userMovedMapAtRef = useRef<number>(0);
  
  // Track if we've set the initial user location to prevent duplicate API calls
  // When navigating to search page with coordinates already set, we don't want to
  // immediately update userlatitude/userlongitude and trigger another API call
  const hasSetInitialUserLocationRef = useRef(false);
  // When user clicks "Go to current location", the next userLocation update should
  // force a full reset of search center (latitude/longitude + unixTimestamp)
  const goToUserLocationRequestedRef = useRef(false);

  // Track if map has completed initial load to prevent onMapMoved from firing during map initialization
  // This prevents the map from overwriting synced location params when the idle event fires on initial load
  const mapHasCompletedInitialLoadRef = useRef(false);
  // Track the last location params that the map was initialized with
  // Only allow onMapMoved to update location if the current params match the initialized params
  const lastInitializedParamsRef = useRef<{lat: string | null, lng: string | null}>({lat: null, lng: null});

  const { selectedVenue, setSelectedVenue } = useSelectedVenue();

  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const [hoveredVenueId, setHoveredVenueId] = useAtom(hoveredVenueIdAtom);

  const { isMapsLoaded } = useLoadGoogleMaps();

  const [mapCenter, setMapCenter] = useAtom(mapCenterAtom);
  const isDark = useDarkMode();
  const { user, isMapper } = useAuth();
  const [viewMode] = useAtom(viewModeAtom);
  const isMapperViewActive = isMapper && viewMode === "mapper";
  
  // Check if we're on the /search route to conditionally skip certain effects
  const matchRoute = useMatchRoute();
  const isSearchRoute = matchRoute({ to: "/search" });

  const { data, refetch } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];

  const markerRefs = useRef(new Map());

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);
  const [listNameCreated, setListNameCreated] = useState("");

  // Listen for custom force zoom event
  useEffect(() => {
    if (mapRef) {
      const handleForceZoom = (
        event: CustomEvent<{ zoom: number; lat: number; lng: number }>,
      ) => {
        const { zoom, lat, lng } = event.detail;
        console.log("Force zoom event received:", zoom, lat, lng);

        // Set the map center and zoom level
        mapRef.setCenter({ lat, lng });
        mapRef.setZoom(zoom);
      };

      window.addEventListener("forceMapZoom", handleForceZoom as EventListener);

      return () => {
        window.removeEventListener(
          "forceMapZoom",
          handleForceZoom as EventListener,
        );
      };
    }
  }, [mapRef]);

  // Sync saved places modal mode with saved places data
  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  // Close snippet when map is moved or zoomed
  useEffect(() => {
    if (mapRef) {
      const closeSnippet = () => {
        setActiveMarker(null);
        setSnippetPosition(null);
        setTooltipVenue(null);
        setHoveredMarkerId(null);
        setSnippetOpen(false);
      };

      // Add listeners for map interactions that should close the snippet
      const dragStartListener = mapRef.addListener("dragstart", closeSnippet);
      const zoomChangedListener = mapRef.addListener(
        "zoom_changed",
        closeSnippet,
      );

      // Cleanup listeners on unmount
      return () => {
        if (dragStartListener) {
          google.maps.event.removeListener(dragStartListener);
        }
        if (zoomChangedListener) {
          google.maps.event.removeListener(zoomChangedListener);
        }
      };
    }
  }, [mapRef]);

  const handleSetLocation = async () => {
    //set user location
    // console.log("inside handleSetLocation");
    setIsLocationModalOpen(false);
    return;
  };

  // Function to show snippet only (no venue loading)
  const handleShowSnippet = (marker: string) => {
    if (marker === activeMarker && snippetOpen) {
      return;
    }

    // Calculate and cache snippet position FIRST before any state changes
    const venue = locations.find((l) => l.id === marker);
    if (!venue) return;

    const pos = getSnippetPosition(venue);

    // Now update all states at once to minimize re-renders
    setTooltipVenue(null);
    setHoveredMarkerId(marker);
    setHoveredVenueId(marker);
    setSnippetPosition(pos);
    setActiveMarker(marker);
    setSnippetOpen(true); // Mark snippet as open
  };

  // Function to load venue in left panel (called from snippet click)
  const handleLoadVenue = (location: Venue) => {
    // Complex venue: open in new tab (matches list click behaviour)
    if (
      location.business?.templateId != null &&
      location.business?.templateId > 1 &&
      location.business?.publicUrl &&
      location.business.publicUrl !== ""
    ) {
      window.open(`/venuedetails/${location.business.publicUrl}`, "_blank");
      handleCloseSnippet(); // close the snippet so the map is left clean
      return;
    }

    // Basic venue: load in side panel
    setMobileListView(true);
    setSelectedVenue(location);
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setSnippetOpen(false); // Close snippet
    // Note: Keep hoveredVenueId for the selected venue
  };

  // Save venue handlers
  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode,
    );

    AnalyticsService.trackGAEventHandler("mySavedPlacesExitSave");
    AnalyticsService.trackMx("mySavedPlacesExitSave", {});

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  // Function to close the snippet without loading venue
  const handleCloseSnippet = () => {
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setHoveredVenueId("");
    setSnippetOpen(false); // Close snippet
  };

  // Function to determine snippet position based on pin location in viewport
  const getSnippetPosition = (location: Venue) => {
    const defaultWidth = 300;
    const defaultHeight = 400;
    const defaultPinHeight = 40;

    if (!mapRef) {
      return {
        offset: {
          x: -(defaultWidth / 2),
          y: -defaultHeight - defaultPinHeight,
        },
        className: "translate-x-[-50%] translate-y-[-105%]",
      };
    }

    try {
      const bounds = mapRef.getBounds();
      if (!bounds) {
        return {
          offset: {
            x: -(defaultWidth / 2),
            y: -defaultHeight - defaultPinHeight,
          },
          className: "translate-x-[-50%] translate-y-[-105%]",
        };
      }

      // Get the map container dimensions
      const mapDiv = mapRef.getDiv();
      const mapHeight = mapDiv.clientHeight;
      const mapWidth = mapDiv.clientWidth;

      // Convert lat/lng to pixel coordinates
      const projection = mapRef.getProjection();
      if (!projection) {
        return {
          offset: {
            x: -(defaultWidth / 2),
            y: -defaultHeight - defaultPinHeight,
          },
          className: "translate-x-[-50%] translate-y-[-105%]",
        };
      }

      const pinLatLng = new google.maps.LatLng(
        location.latitude,
        location.longitude,
      );
      const scale = Math.pow(2, mapRef.getZoom());
      const worldCoordinate = projection.fromLatLngToPoint(pinLatLng);
      const pinPixelY = worldCoordinate.y * scale;
      const pinPixelX = worldCoordinate.x * scale;

      // Get the map's center pixel position
      const centerLatLng = mapRef.getCenter();
      const centerWorldCoordinate = projection.fromLatLngToPoint(centerLatLng);
      const centerPixelY = centerWorldCoordinate.y * scale;
      const centerPixelX = centerWorldCoordinate.x * scale;

      // Calculate the pin's pixel position relative to the map center
      const pinRelativeY = pinPixelY - centerPixelY;
      const pinRelativeX = pinPixelX - centerPixelX;

      // Pin is at center of map, so its absolute position in viewport
      const pinViewportY = mapHeight / 2 + pinRelativeY;
      const pinViewportX = mapWidth / 2 + pinRelativeX;

      // Snippet actual dimensions
      const actualSnippetHeight = 400; // Actual height of the VenueCard snippet
      const actualSnippetWidth = 300; // Actual width of the VenueCard snippet

      // Snippet dimensions for space calculations (with buffer for shadows, borders, and safety)
      const snippetHeight = 440; // Height + extra buffer for shadows and top safety
      const snippetWidth = 320; // Width + extra buffer for shadows
      const pinHeight = 40; // Height of the pin marker
      const marginHorizontal = 50; // Increased safety margin from left/right edges
      const marginTop = 80; // Extra margin for top edge (more critical due to browser chrome)
      const marginBottom = 50; // Standard margin for bottom edge

      // Calculate available space on all sides
      const spaceAbove = pinViewportY - marginTop;
      const spaceBelow = mapHeight - pinViewportY - pinHeight - marginBottom;
      const spaceLeft = pinViewportX - marginHorizontal;
      const spaceRight = mapWidth - pinViewportX - marginHorizontal;

      // Determine if snippet fits in each direction with buffer
      // For top positioning, add extra 30px buffer to be more conservative
      const fitsAbove = spaceAbove >= snippetHeight + 30;
      const fitsBelow = spaceBelow >= snippetHeight;

      // For horizontal positioning, check if there's space for the full snippet width
      // when centered (need half width on each side) - with extra buffer
      const fitsCentered =
        spaceLeft >= snippetWidth / 2 + 10 &&
        spaceRight >= snippetWidth / 2 + 10;

      // Check if full snippet can fit to the right (when aligned left from pin) - with extra buffer
      const fitsFullyRight = spaceRight >= snippetWidth + 10;

      // Check if full snippet can fit to the left (when aligned right from pin) - with extra buffer
      const fitsFullyLeft = spaceLeft >= snippetWidth + 10;

      // Determine vertical position (above or below) - use actual dimensions for offset
      // Add extra offset padding for top positioning to prevent cutoff
      let yOffset = -actualSnippetHeight - pinHeight - 10; // Extra 10px padding when above
      let translateY = "translate-y-[-105%]";

      if (fitsAbove && !fitsBelow) {
        yOffset = -actualSnippetHeight - pinHeight - 10; // Extra padding
        translateY = "translate-y-[-105%]";
      } else if (fitsBelow && !fitsAbove) {
        yOffset = pinHeight + 5; // Small padding below too
        translateY = "translate-y-[5%]";
      } else if (!fitsAbove && !fitsBelow) {
        // Neither fits perfectly, strongly prefer below to avoid top cutoff
        if (spaceBelow > spaceAbove - 50) {
          // Bias towards below by 50px
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      } else {
        // Both fit, but prefer below if pin is in top 40% of screen to avoid top edge
        const pinInTopPortion = pinViewportY < mapHeight * 0.4;
        if (pinInTopPortion) {
          // Pin is near top, prefer showing below
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          // Pin has plenty of space above, show above
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      }

      // Determine horizontal position (center, left, or right aligned) - use actual dimensions for offset
      let xOffset = -(actualSnippetWidth / 2); // default: centered
      let translateX = "translate-x-[-50%]";

      if (fitsCentered) {
        // Snippet fits centered, keep default
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      } else if (!fitsFullyRight && fitsFullyLeft) {
        // Too close to right edge, align snippet to the LEFT of the pin
        xOffset = -actualSnippetWidth;
        translateX = "translate-x-[-100%]";
      } else if (!fitsFullyLeft && fitsFullyRight) {
        // Too close to left edge, align snippet to the RIGHT of the pin
        xOffset = 0;
        translateX = "translate-x-[0%]";
      } else if (!fitsFullyLeft && !fitsFullyRight) {
        // Doesn't fit on either side fully, choose side with more space
        if (spaceRight > spaceLeft) {
          xOffset = 0;
          translateX = "translate-x-[0%]";
        } else {
          xOffset = -actualSnippetWidth;
          translateX = "translate-x-[-100%]";
        }
      } else {
        // Both sides fit, prefer centered (already set as default)
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      }

      return {
        offset: { x: xOffset, y: yOffset },
        className: `${translateX} ${translateY}`,
      };
    } catch (error) {
      console.error("Error calculating snippet position:", error);
      return {
        offset: {
          x: -(defaultWidth / 2),
          y: -defaultHeight - defaultPinHeight,
        },
        className: "translate-x-[-50%] translate-y-[-105%]",
      };
    }
  };

  const {
    location: userLocation,
    loading: locationLoading,
    error: locationError,
    requestUserLocation,
  } = useGeoLocation();

  // Result of search api
  // Note: We always call useHomeVenues() (React hooks rules), but when locationsOverride
  // is provided (e.g., from search page), we use that instead to avoid redundant data usage
  const { locations: locationsFromHook = [], isLoading, data: venuesData } = useHomeVenues();
  
  // Use override locations if provided (search page passes filtered locations),
  // otherwise use locations from hook (saved places/venue details pages fetch their own data)
  const locations = locationsOverride || locationsFromHook;

  const { setSearchPageParams, searchPageParams } = useSearchPageParams();

  useEffect(() => {

    // Skip this effect on the /search route
    if (isSearchRoute && !goToUserLocationRequestedRef.current) {
      return;
    }

    // console.log("goToUserLocationRequestedRef", goToUserLocationRequestedRef.current);

    if (userLocation && mapRef) {
      // User explicitly clicked "Go to current location" � force full reset of search center
      if (goToUserLocationRequestedRef.current) {
        mapRef.panTo({
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        });
        setMapCenter({ lat: userLocation.latitude, lng: userLocation.longitude });
        setSearchPageParams((prev) => ({
          ...prev,
          userlatitude: userLocation.latitude.toString(),
          userlongitude: userLocation.longitude.toString(),
          latitude: userLocation.latitude.toString(),
          longitude: userLocation.longitude.toString(),
          zoom: mapRef?.getZoom()?.toString() || "14",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        }));
        goToUserLocationRequestedRef.current = false;
        return;
      }

      setSearchPageParams((prev) => {
        // Check if latitude and longitude are already set (e.g., from shared search or custom location)
        const hasExistingCoordinates =
          prev.latitude !== null && prev.longitude !== null && prev.latitude !== undefined && prev.longitude !== undefined;

        if (savedPlaceIdToView == "") {
          if (hasExistingCoordinates) {
            // Pan to the existing custom coordinates (shared from home page)
            const safeLat =
              parseFloat(prev.latitude || "") || defaultRegion.latitude;
            const safeLng =
              parseFloat(prev.longitude || "") || defaultRegion.longitude;
            mapRef.panTo({
              lat: safeLat,
              lng: safeLng,
            });
          } else {
            // No existing coordinates, use user's geolocation
            mapRef.panTo({
              lat: userLocation.latitude,
              lng: userLocation.longitude,
            });
          }
        }

        // If coordinates already exist AND this is the initial load, skip updating userlatitude/userlongitude
        // to prevent triggering an extra API call when navigating from home page with search params
        if (hasExistingCoordinates && !hasSetInitialUserLocationRef.current) {
          console.log('[BasicMapView] Skipping initial userLocation update to prevent duplicate API call');
          hasSetInitialUserLocationRef.current = true;
          return prev; // Return unchanged to avoid triggering API call
        }

        // If coordinates already exist, only update user location fields (not search center)
        if (hasExistingCoordinates) {
          return {
            ...prev,
            userlatitude: userLocation.latitude.toString(),
            userlongitude: userLocation.longitude.toString(),
            // DO NOT update latitude/longitude - preserve the custom location
            zoom: mapRef?.getZoom()?.toString() || "14",
            saved_place_id: savedPlaceIdToView,
            saved_place_user_id: savedPlaceByUserIdToView,
            saved_place_sortby: savedPlaceSortByToView,
            // Don't update unixTimestamp to avoid unnecessary API calls
          };
        }

        // No existing coordinates - set everything including search center
        hasSetInitialUserLocationRef.current = true;
        return {
          ...prev,
          userlatitude: userLocation.latitude.toString(),
          userlongitude: userLocation.longitude.toString(),
          latitude: userLocation.latitude.toString(),
          longitude: userLocation.longitude.toString(),
          zoom: mapRef?.getZoom()?.toString() || "14",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        };
      });
    }
  }, [
    userLocation,
    mapRef,
    setSearchPageParams,
    setMapCenter,
    savedPlaceIdToView,
    savedPlaceByUserIdToView,
    savedPlaceSortByToView,
  ]);

  useEffect(() => {
    // Skip this effect on the /search route
    if (isSearchRoute) {
      return;
    }

    if (locationError) {
      if (mapRef) {
        console.log(
          "Setting default location and loading pins savedPlaceIdToView",
          savedPlaceIdToView,
          "savedPlaceByUserIdToView",
          savedPlaceByUserIdToView,
        );
        setSearchPageParams((prev) => {
          // Check if latitude and longitude are already set (e.g., from shared search or custom location)
          const hasExistingCoordinates =
            prev.latitude !== null && prev.longitude !== null && prev.latitude !== undefined && prev.longitude !== undefined;

          if (savedPlaceIdToView == "") {
            if (hasExistingCoordinates) {
              // Pan to the existing custom coordinates (shared from home page)
              const safeLat =
                parseFloat(prev.latitude || "") || defaultRegion.latitude;
              const safeLng =
                parseFloat(prev.longitude || "") || defaultRegion.longitude;
              mapRef.panTo({
                lat: safeLat,
                lng: safeLng,
              });
            } else {
              // No existing coordinates, use default region
              mapRef.panTo({
                lat: defaultRegion.latitude,
                lng: defaultRegion.longitude,
              });
            }
          }

          // If coordinates already exist AND this is the initial load, skip updating userlatitude/userlongitude
          if (hasExistingCoordinates && !hasSetInitialUserLocationRef.current) {
            console.log('[BasicMapView] Skipping initial location error fallback update to prevent duplicate API call');
            hasSetInitialUserLocationRef.current = true;
            return prev; // Return unchanged to avoid triggering API call
          }

          // If coordinates already exist, only update user location fields (not search center)
          if (hasExistingCoordinates) {
            return {
              ...prev,
              userlatitude: defaultRegion.latitude.toString(),
              userlongitude: defaultRegion.longitude.toString(),
              // DO NOT update latitude/longitude - preserve the custom location
              zoom: mapRef?.getZoom()?.toString() || "14",
              saved_place_id: savedPlaceIdToView,
              saved_place_user_id: savedPlaceByUserIdToView,
              saved_place_sortby: savedPlaceSortByToView,
              // Don't update unixTimestamp to avoid unnecessary API calls
            };
          }

          // No existing coordinates - set everything including search center
          hasSetInitialUserLocationRef.current = true;
          return {
            ...prev,
            userlatitude: defaultRegion.latitude.toString(),
            userlongitude: defaultRegion.longitude.toString(),
            latitude: defaultRegion.latitude.toString(),
            longitude: defaultRegion.longitude.toString(),
            zoom: mapRef?.getZoom()?.toString() || "14",
            saved_place_id: savedPlaceIdToView,
            saved_place_user_id: savedPlaceByUserIdToView,
            saved_place_sortby: savedPlaceSortByToView,
            unixTimestamp: Math.floor(Date.now() / 1000),
          };
        });
      }
    }
  }, [
    isSearchRoute,
    locationError,
    mapRef,
    savedPlaceByUserIdToView,
    savedPlaceIdToView,
    savedPlaceSortByToView,
    setSearchPageParams,
    setMapCenter,
  ]);

  const onMapMoved = () => {
    // Always keep mapCenterAtom in sync with the actual map position
    // so that SearchBar and other components can use the current map center    
    // below code create issue where info window open away from pins
    // const currentCenter = mapRef?.getCenter();
    // if (currentCenter) {
    //   setMapCenter({ lat: currentCenter.lat(), lng: currentCenter.lng() });
    // }

    // Skip if map hasn't completed initial load yet
    // This prevents overwriting synced location params when the idle event fires on initial map load
    if (!mapHasCompletedInitialLoadRef.current) {
      console.log("Skipping onMapMoved - map still initializing");
      // Mark as completed and store the current params
      // This ensures we only respond to REAL user movements, not initial map setup
      mapHasCompletedInitialLoadRef.current = true;
      lastInitializedParamsRef.current = {
        lat: searchPageParams.latitude,
        lng: searchPageParams.longitude
      };
      console.log("Map initialization complete - onMapMoved now enabled for params:", lastInitializedParamsRef.current);
      return;
    }

    // Additional check: If searchPageParams have changed since we initialized,
    // skip this event (it's from the previous location's map settling)
    if (lastInitializedParamsRef.current.lat !== searchPageParams.latitude ||
        lastInitializedParamsRef.current.lng !== searchPageParams.longitude) {
      console.log("Skipping onMapMoved - searchPageParams changed since map initialization");
      // Update to new params and wait for next idle
      lastInitializedParamsRef.current = {
        lat: searchPageParams.latitude,
        lng: searchPageParams.longitude
      };
      return;
    }

    if (!mapSettings?.mapUpdate) {
      // Progressive search is disabled via mapSettings
      return;
    }

    if (savedPlaceIdToView !== "") {
      console.log("No progresive search on View saved places");
      return;
    }

    if (activeMarker) {
      // console.log("Marker active not loading new pins");
      return;
    }

    //check how much the map moved
    //if the map moved more than 1km, then fetch the venues
    //if the map moved less than 1km, then don't fetch the venues
    const prevCenter = new google.maps.LatLng(
      parseFloat(searchPageParams.latitude),
      parseFloat(searchPageParams.longitude),
    );
    const newCenter = mapRef?.getCenter();

    const distanceInMeters =
      google.maps.geometry.spherical.computeDistanceBetween(
        prevCenter,
        newCenter,
      );
    //console.log("Distance changed:", distanceInMeters);
    if (
      distanceInMeters >
      parseFloat(mapSettings?.mapUpdateDistance || "0.5") * 1000
    ) {
      console.log(
        "onMapMoved Reloading pins",
        newCenter?.lat().toString(),
        newCenter?.lng().toString(),
      );
      // Mark that the user moved the map to suppress auto-zoom briefly
      userMovedMapAtRef.current = Date.now();
      setSearchPageParams((prev) => {
        const params = {
          ...prev,
          latitude: newCenter?.lat().toString() || "",
          longitude: newCenter?.lng().toString() || "",
          zoom: mapRef?.getZoom()?.toString() || "14",
          searchThisAreaClicked: "no",
          unixTimestamp: Math.floor(Date.now() / 1000),
          includeGoogleFallback: "false",
        };
        // Clear expand_radius specifically � other search types (brand, venue_name, etc.) should persist
        if (prev.search_type === "expand_radius") {
          delete params.search_type;
          delete params.search_value;
        }
        return params;
      });
    } else {
      // console.log("Not reloading pins");
    }
  };

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMapRef(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    setMapRef(null);
  }, []);

  const getPinIcon = (venue: Venue) => {
    // console.log("venue.venuePinType isMapperViewActive ", isMapperViewActive, venue.venuePinType);
    // User view: Only show Orange/Grey pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPin;
      } else {
        return OrangePin;
      }
    }

    // Mapper view: Show all 6 pin types
    switch (venue.venuePinType) {
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      case VenuePinType.Orange:
        return OrangePin;
      case VenuePinType.Grey:
        return GreyPin;
      default:
        return GreyPin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey selected pins
    // if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPinSelected;
      } else {
        return OrangePinSelected;
      }
    // }

    // // Mapper view: Show all pin types (selected variants only for Grey/Orange)
    // switch (venue.venuePinType) {
    //   case VenuePinType.Grey:
    //     return GreyPinSelected;
    //   case VenuePinType.Orange:
    //     return OrangePinSelected;
    //   case VenuePinType.Green:
    //     return GreenPin;
    //   case VenuePinType.Black:
    //     return BlackPin;
    //   case VenuePinType.Blue:
    //     return BluePin;
    //   case VenuePinType.Purple:
    //     return PurplePin;
    //   default:
    //     return GreyPinSelected;
    // }
  };

  const radius = 75; // default radius in km
  const unit = "km"; // or'miles'
  const toRadians = (degrees) => {
    return degrees * (Math.PI / 180);
  };

  // Ref to track which venuesData we already auto-zoomed for, so we don't
  // re-run auto-zoom (and trigger another API call) for the same result set
  const lastAutoZoomedDataRef = useRef<any>(null);

// Effect to auto-update zoom level based on search_distance_used (gated by flag)
  useEffect(() => {
    console.log("search_distance_used", venuesData?.search_distance_used);
    console.log(
      "search_original_distance",
      venuesData?.search_original_distance,
    );

    // Only auto-zoom when explicitly flagged by search click
    if (mapRef && enableAutoZoom && searchPageParams.autoZoom === "yes") {
      // Skip if we already auto-zoomed for this exact data set
      if (venuesData === lastAutoZoomedDataRef.current) {
        return;
      }
      lastAutoZoomedDataRef.current = venuesData;

      // Case 1: API returned different distances - adjust zoom based on search_distance_used
      if (
        venuesData?.search_distance_used &&
        venuesData.search_distance_used !== venuesData.search_original_distance
      ) {
        console.log(
          "Received modified search distance:",
          venuesData.search_distance_used,
        );

        const distanceInKm = venuesData.search_distance_used;

        // Calculate the appropriate zoom level
        const newZoomLevel = calculateZoomFromDistance(distanceInKm);
        console.log(
          "Calculated zoom level:",
          newZoomLevel,
          "from distance:",
          distanceInKm,
          "km",
        );

        // Only update if we need to zoom OUT (expand view)
        // Lower zoom number = zoomed out (wider area), higher = zoomed in (closer)
        // Only allow zooming out, never zoom in
        const currentZoom = mapRef.getZoom();
        if (newZoomLevel < currentZoom) {
          console.log("Zooming OUT from", currentZoom, "to", newZoomLevel);

          // Only update the map visually � do NOT call setSearchPageParams
          // to avoid triggering another API call
          mapRef.setZoom(newZoomLevel);
        } else {
          console.log("Skipping zoom IN - current zoom", currentZoom, "is already closer than", newZoomLevel);
        }
      }
      // Case 2: No distance information - use fitBounds to show all venues (only zoom OUT, never IN)
      else if (
        venuesData &&
        locations.length > 0 &&
        !venuesData.search_distance_used &&
        !venuesData.search_original_distance
      ) {
        console.log(
          "No distance information received, checking if fitBounds should apply",
        );

        // Zoom constraints (same as home page)
        const MIN_ZOOM = 11; // Don't zoom out beyond this
        const MAX_ZOOM = 16; // Don't zoom in beyond this

        try {
          const currentZoom = mapRef.getZoom();
          console.log("Current zoom level:", currentZoom);

          // Create bounds from all venue locations
          const bounds = new google.maps.LatLngBounds();
          locations.forEach((venue) => {
            if (venue.latitude && venue.longitude) {
              bounds.extend(
                new google.maps.LatLng(venue.latitude, venue.longitude),
              );
            }
          });

          if (bounds.isEmpty()) {
            console.log("Bounds are empty, skipping fitBounds");
            return;
          }

          // Calculate the distance/spread of venues to estimate zoom level
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          
          // Calculate approximate distance in km using Haversine formula
          const latDiff = ne.lat() - sw.lat();
          const lngDiff = ne.lng() - sw.lng();
          const avgLat = (ne.lat() + sw.lat()) / 2;
          
          // Rough distance calculation (good enough for zoom estimation)
          const latDistance = latDiff * 111; // 1 degree lat � 111 km
          const lngDistance = lngDiff * 111 * Math.cos(avgLat * Math.PI / 180);
          const maxDistance = Math.max(latDistance, lngDistance);
          
          // Calculate what zoom level would be appropriate for this distance
          const estimatedZoom = calculateZoomFromDistance(maxDistance * 1.2); // Add 20% margin
          console.log("Estimated zoom for bounds:", estimatedZoom, "from distance:", maxDistance.toFixed(2), "km");

          // Only apply fitBounds if it would zoom OUT (lower number = more zoomed out)
          // or if this is the initial load (currentZoom is undefined)
          if (currentZoom === undefined || estimatedZoom < currentZoom) {
            console.log(
              "Applying fitBounds - zooming OUT from",
              currentZoom,
              "to approximately",
              estimatedZoom,
            );

            // Apply fitBounds with padding
            mapRef.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50,
            });

            // Enforce min/max zoom constraints after fitBounds
            setTimeout(() => {
              const actualZoom = mapRef.getZoom();
              if (actualZoom !== undefined) {
                if (actualZoom < MIN_ZOOM) {
                  mapRef.setZoom(MIN_ZOOM);
                  console.log("Zoom limited to minimum:", MIN_ZOOM);
                } else if (actualZoom > MAX_ZOOM) {
                  mapRef.setZoom(MAX_ZOOM);
                  console.log("Zoom limited to maximum:", MAX_ZOOM);
                } else {
                  console.log("Dynamic zoom applied:", actualZoom);
                }
              }
            }, 50); // Minimal delay to allow fitBounds to complete
          } else {
            console.log(
              "Respecting manual zoom - staying at",
              currentZoom,
              "(fitBounds would zoom IN to approximately",
              estimatedZoom,
              ")",
            );
          }
        } catch (error) {
          console.error("Error applying dynamic fitBounds zoom:", error);
        }
      }
    }
  }, [
    venuesData,
    locations.length,
    mapRef,
    searchPageParams.autoZoom,
  ]);

  // Effect to pan the map to the first result for expand_radius searches
  // This is separate from auto-zoom � it only handles map centering
  useEffect(() => {
    if (
      mapRef &&
      searchPageParams.search_type === "expand_radius" &&
      locations.length > 0
    ) {
      const firstResult = locations[0];
      if (firstResult?.latitude && firstResult?.longitude) {
        const resultCenter = {
          lat: firstResult.latitude,
          lng: firstResult.longitude,
        };
        console.log("expand_radius: Panning map to first result", resultCenter);
        mapRef.panTo(resultCenter);
        setMapCenter(resultCenter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, mapRef, searchPageParams.search_type]);


  // Reset map initialization flag when location changes significantly
  // This allows onMapMoved to be skipped during the next map load
  useEffect(() => {
    // Reset the flag when searchPageParams location changes
    // This ensures we skip onMapMoved during the new map initialization
    mapHasCompletedInitialLoadRef.current = false;
    lastInitializedParamsRef.current = {lat: null, lng: null};
    console.log("Location changed - resetting map initialization flags");
  }, [searchPageParams.latitude, searchPageParams.longitude]);

  useEffect(() => {
    if (
      locations.length &&
      mapRef &&
      userLocation &&
      savedPlaceIdToView !== ""
    ) {
      const calculateDistance = (lat1, lon1, lat2, lon2, unit) => {
        const R = unit === "miles" ? 3958.8 : 6371; // Radius of the Earth in miles or km
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
      };

      const filterLocationsByRadius = (locations, center, radius, unit) => {
        return locations.filter((location) => {
          const distance = calculateDistance(
            center.latitude,
            center.longitude,
            location.latitude,
            location.longitude,
            unit,
          );
          return distance <= radius;
        });
      };

      const filteredLocations = filterLocationsByRadius(
        locations,
        userLocation,
        radius,
        unit,
      );
      const bounds = new google.maps.LatLngBounds();
      filteredLocations.forEach((location) => {
        bounds.extend(
          new google.maps.LatLng(location.latitude, location.longitude),
        );
      });

      bounds.extend(
        new google.maps.LatLng(userLocation.latitude, userLocation.longitude),
      );

      mapRef.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, userLocation, radius, unit, mapRef]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  // mapId: googleMapId
  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    ...(isDark
      ? { styles: darkModeStyles } // Add dark mode styles if isDark is true
      : { styles: lightModeStyles }), // Add mapId if isDark is false
  };

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block",
      )}
    >
      {isMapsLoaded && (
        <>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%",
            }}
            center={mapCenter}
            zoom={16}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions} // Pass the dynamic mapOptions here
            //onDragEnd={() => onMapMoved()}
            onClick={() => {
              setActiveMarker(null);
              setSnippetPosition(null);
              setTooltipVenue(null);
              setHoveredMarkerId(null);
              setHoveredVenueId("");
              setSnippetOpen(false);
            }}
            onIdle={() => onMapMoved()}
          >
            {locations.map((location, index) => (
              <Marker
                ref={(marker) => {
                  if (marker) {
                    markerRefs.current.set(location.id, marker);
                  }
                }}
                key={`marker-${location.id}-${index}`}
                position={{
                  lat: location.latitude,
                  lng: location.longitude,
                }}
                title={location.name || "Venue location"}
                zIndex={
                  selectedVenue && selectedVenue.id === location.id
                    ? 1000
                    : index
                }
                icon={{
                  url:
                    selectedVenue && selectedVenue.id === location.id
                      ? getSelectedPinIcon(location)
                      : getPinIcon(location),
                  anchor: new google.maps.Point(15, 30),
                  size:
                    selectedVenue && selectedVenue.id === location.id
                      ? new google.maps.Size(40, 40)
                      : hoveredMarkerId === location.id ||
                          hoveredVenueId === location.id ||
                          activeMarker === location.id
                        ? new google.maps.Size(42, 42)
                        : new google.maps.Size(30, 30),
                  scaledSize:
                    selectedVenue && selectedVenue.id === location.id
                      ? new google.maps.Size(40, 40)
                      : hoveredMarkerId === location.id ||
                          hoveredVenueId === location.id ||
                          activeMarker === location.id
                        ? new google.maps.Size(42, 42)
                        : new google.maps.Size(30, 30),
                }}
                onMouseOver={() => {
                  // If a snippet is already open, ignore all hover events
                  if (snippetOpen) {
                    return;
                  }
                  // Only show tooltip if no snippet is open
                  setTooltipVenue(location);
                  setHoveredVenueId(location.id);
                  setHoveredMarkerId(location.id);
                }}
                onMouseOut={() => {
                  // If a snippet is open, ignore all mouse out events
                  if (snippetOpen) {
                    return;
                  }
                  // Only clear states if no snippet is open
                  setTooltipVenue(null);
                  setHoveredVenueId("");
                  setHoveredMarkerId(null);
                }}
                onClick={() => {
                  // TODO: Panning check commented out since we're not panning
                  // if (isPanning) return;

                  AnalyticsService.trackGAEventHandler(
                    "homePickedVenueFromSearchResultsMap",
                  );
                  AnalyticsService.trackMx(
                    "homePickedVenueFromSearchResultsMap",
                    {
                      VenueId: location.business?.id,
                      VenueBrandId: location.brandId,
                      VenueBrandName: location.brandName,
                      VenueAddress: location.address,
                      VenueName: location.name,
                      VenueType: location.businessType,
                    },
                  );

                  // On home page (currentVenueTypePage === 0): show snippet first, then user clicks snippet to load venue
                  // On venue details page (currentVenueTypePage === 1): directly load venue, skip snippet
                  if (
                    currentVenueTypePage === 1 ||
                    props.mapSource === "venuedetails"
                  ) {
                    handleLoadVenue(location);
                  } else {
                    handleShowSnippet(location.id);
                  }
                }}
              >
                {currentVenueTypePage === 0 &&
                !selectedVenue &&
                activeMarker === location.id &&
                markerRefs.current?.get(activeMarker) &&
                snippetPosition
                  ? (() => {
                      return (
                        <>
                          {/* Desktop: Show snippet near pin */}
                          <OverlayView
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            position={{
                              lat: location.latitude,
                              lng: location.longitude,
                            }}
                            getPixelPositionOffset={() =>
                              snippetPosition.offset
                            }
                          >
                            {/* Wrapper with pointer-events-none to not block map */}
                            <div
                              className={`hidden lg:block w-[300px] ${snippetPosition.className}`}
                              style={{ pointerEvents: "none" }}
                            >
                              <div
                                className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full"
                                style={{ pointerEvents: "auto" }}
                              >
                                <style>{`
                                  .map-snippet-wrapper button.bg-opacity-30 {
                                  right: 52px !important;
                                  }`}</style>

                                {/* Close button - positioned at right edge */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseSnippet();
                                  }}
                                  className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                                  aria-label="Close venue preview"
                                  type="button"
                                >
                                  <X
                                    size={16}
                                    weight="bold"
                                    className="text-fg-neutral-strong"
                                  />
                                </button>

                                <button
                                  type="button"
                                  className="w-full text-left rounded p-0 focus-blue pointer-events-auto cursor-pointer"
                                  aria-label={`View details for ${location.name}`}
                                >
                                  <VenueCard
                                    location={location}
                                    onOpen={() => handleLoadVenue(location)}
                                    onSaveVenue={handleSaveVenue}
                                    savedPlaces={savedPlaces}
                                    truncateText={true}
                                    index={index}
                                    totalCount={locations.length}
                                    showMetaInfo={true}
                                    showOpeningHours={true}
                                    showVariantIndicators={true}
                                    showDescription={true}
                                    showAddress={false}
                                    inMapView={true}
                                  />
                                </button>
                              </div>
                            </div>
                          </OverlayView>
                        </>
                      );
                    })()
                  : null}
              </Marker>
            ))}
            {userLocation && (
              <Marker
                key={"userLocation"}
                position={{
                  lat: userLocation.latitude,
                  lng: userLocation.longitude,
                }}
                title={i18n.get("yourLocation") || "Your current location"}
                zIndex={10000}
                icon={{
                  url: YouAre,
                  anchor: new google.maps.Point(15, 30),
                  size: new google.maps.Size(30, 30),
                  scaledSize: new google.maps.Size(30, 30),
                }}
              ></Marker>
            )}

            {/* Hover tooltip - only show when no snippet is open */}
            {(() => {
              if (snippetOpen) return null; // Don't show tooltip when snippet is open
              const displayVenue =
                tooltipVenue ||
                (hoveredVenueId
                  ? locations.find((l) => l.id === hoveredVenueId)
                  : null);
              return displayVenue ? (
                <OverlayView
                  position={{
                    lat: displayVenue.latitude,
                    lng: displayVenue.longitude,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -height - 50,
                  })}
                >
                  <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-2 rounded-lg shadow-lg pointer-events-none w-max max-w-[400px]">
                    <div className="label-m-medium whitespace-nowrap">
                      {displayVenue.name}
                    </div>
                  </div>
                </OverlayView>
              ) : null;
            })()}
          </GoogleMap>

          {/* TODO: Invisible overlay commented out since we're not panning */}
          {/* {isPanning && (
 <div
 className="absolute inset-0 z-[1000] cursor-wait"
 style={{ pointerEvents:'all' }}
 />
 )} */}
        </>
      )}
      {/* Show nearby */}
      {savedPlaceIdToView === "" && (
        <div className="absolute left-0 right-0 top-2 flex items-center justify-center pointer-events-none">
          <StandardButton
            variant="secondary"
            className="pointer-events-auto rounded-[8px] px-[16px] py-[8px] shadow-md"
            onClick={() => {
              AnalyticsService.trackGAEventHandler(
                "homeSwitchedToSearchThisArea",
              );
              AnalyticsService.trackMx("homeSwitchedToSearchThisArea", {});

              // Get current map center
              const currentCenter = mapRef.getCenter();
              const currentLat = currentCenter.lat();
              const currentLng = currentCenter.lng();

              // Update mapCenter atom to keep it in sync
              // This ensures geocoding uses the correct location when user types a new search term
              setMapCenter({ lat: currentLat, lng: currentLng });

              setSearchPageParams((prev) => {
                const params = {
                  ...prev,
                  latitude: currentLat.toString(),
                  longitude: currentLng.toString(),
                  userlatitude: userLocation?.latitude.toString() || "",
                  userlongitude: userLocation?.longitude.toString() || "",
                  searchThisAreaClicked: "yes",
                  zoom: mapRef?.getZoom().toString() || "15",
                  saved_place_id: savedPlaceIdToView,
                  saved_place_user_id: savedPlaceByUserIdToView,
                  saved_place_sortby: savedPlaceSortByToView,
                  unixTimestamp: Math.floor(Date.now() / 1000),
                  includeGoogleFallback: "false",
                };
                // Clear expand_radius specifically � other search types (brand, venue_name, etc.) should persist
                if (prev.search_type === "expand_radius") {
                  delete params.search_type;
                  delete params.search_value;
                }
                return params;
              });
            }}
          >
            <span className="w-6 h-6 relative mr-1">
              <FiMapPin size={24} />
            </span>
            {searchPageParams.search ? i18n.get("searchThisArea") : i18n.get("showNearBy")}
          </StandardButton>
        </div>
      )}
      {/* Goto current location, +, - */}
      <div className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end pointer-events-none">
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            AnalyticsService.trackGAEventHandler("homeZoomedInMapView");
            AnalyticsService.trackMx("homeZoomedInMapView", {});
            mapRef.getZoom() < 17 && mapRef.setZoom(mapRef.getZoom() + 1);
          }}
          className="p-2 mb-1 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("zoomIn") || "Zoom in"}
        >
          <FaPlus size={24} color={isDark ? "white" : "#121042"} aria-hidden="true" />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            AnalyticsService.trackGAEventHandler("homeZoomOutMapView");
            AnalyticsService.trackMx("homeZoomOutMapView", {});
            mapRef.getZoom() > 0 && mapRef.setZoom(mapRef.getZoom() - 1);
          }}
          className="p-2 mb-8 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("zoomOut") || "Zoom out"}
        >
          <FaMinus size={24} color={isDark ? "white" : "#121042"} aria-hidden="true" />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            console.log("Requesting location");
            AnalyticsService.trackGAEventHandler("homeGeolocatedOnMap");
            AnalyticsService.trackMx("homeGeolocatedOnMap", {});

            // Conditional logic based on locationError
            if (locationError) {
              setIsLocationModalOpen(true);
            } else {
              goToUserLocationRequestedRef.current = true;
              requestUserLocation();
            }
          }}
          className="shadow-md p-2 mb-4 mr-2 flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("goToCurrentLocation") || "Go to current location"}
        >
          <TbCurrentLocation size={24} color={isDark ? "white" : "#121042"} aria-hidden="true" />
        </StandardButton>
      </div>
      {(isLoading || locationLoading) && (
        <Loader
          includeGoogleFallback={
            searchPageParams.includeGoogleFallback === "true"
          }
        />
      )}

      {/* Mobile: Fixed bottom snippet */}
      {currentVenueTypePage === 0 &&
        !selectedVenue &&
        activeMarker &&
        (() => {
          const location = locations.find((l) => l.id === activeMarker);
          const index = locations.findIndex((l) => l.id === activeMarker);
          if (!location) return null;

          return (
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up pointer-events-none">
              <style>{`
 @keyframes slide-up {
 from {
 transform: translateY(100%);
 }
 to {
 transform: translateY(0);
 }
 }
 .animate-slide-up {
 animation: slide-up 0.3s ease-out;
 }
 .mobile-snippet-wrapper button.bg-opacity-30 {
 right: 52px !important;
 }
 .mobile-snippet-container {
 max-height: 280px;
 overflow: hidden;
 }
 .mobile-snippet-wrapper figure {
 aspect-ratio: 16 / 7 !important;
 max-height: 140px !important;
 }
 .mobile-snippet-wrapper figure img {
 object-fit: cover !important;
 }`}</style>

              <div className="mobile-snippet-container relative">
                {/* Close button - positioned at right edge */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseSnippet();
                  }}
                  className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                  aria-label="Close venue preview"
                  type="button"
                >
                  <X
                    size={16}
                    weight="bold"
                    className="text-fg-neutral-strong"
                  />
                </button>

                <button
                  type="button"
                  className="w-full text-left rounded p-0 focus-blue mobile-snippet-wrapper cursor-pointer"
                  aria-label={`View details for ${location.name}`}
                >
                  <VenueCard
                    location={location}
                    onOpen={() => handleLoadVenue(location)}
                    onSaveVenue={handleSaveVenue}
                    savedPlaces={savedPlaces}
                    truncateText={true}
                    index={index}
                    totalCount={locations.length}
                    showMetaInfo={true}
                    showOpeningHours={true}
                    showVariantIndicators={true}
                    showDescription={true}
                    showAddress={false}
                    inMapView={true}
                  />
                </button>
              </div>
            </div>
          );
        })()}

      <ModalWithAction
        isModalOpen={isLocationModalOpen}
        title={i18n.get("loctionSettingsRequired")}
        subtitle={i18n.get("loctionSettingsRequiredMessage")}
        cancelButtonTitle={""}
        actionButtonTitle={i18n.get("ok")}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        onClick={handleSetLocation}
        type="default"
        actionButtonType="default"
      />

      {/* Saved Places Modal */}
      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={locations.find(
                (l) => l.business.id === locationForSavedPlaces,
              )}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={locations.find(
                (l) => l.business.id === locationForSavedPlaces,
              )}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

export default BasicMapView;
